// libs
import { useContext, useMemo } from "react";

// internal deps
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import {
    CheckoutWebDynamicConfigContext,
    CHECKOUT_WEB_WP,
    CHECKOUT_WEB_WP_CONFIG_KEYS,
    CheckoutWebWPConfig,
    ConfigContext,
    isBusinessCategoryInstamart,
} from "@swiggy-private/checkout-core";
import { safeJsonParse } from "@swiggy-private/js-utils";

// helpers
import { isPaymentInstrumentAvailable } from "@payment-client/helpers/PaymentHelper";
import { getMerchantId } from "@payment-client/helpers/JuspayHelpers";
// hooks
import { useJuspaySdkState } from "./UseJuspaySdkState";

// contexts
import { JuspayContextValue } from "@payment-client/contexts/JuspayContext";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { ActiveTenant } from "@payment-client/constants/tenant";

const useJuspaySdk = (paymentResponse: GetPaymentMethodsResponse): JuspayContextValue => {
    const { paymentGroup } = paymentResponse;

    const merchantId = useMemo(() => getMerchantId(paymentResponse), [paymentResponse]);

    const isCredPayGroupExists = useMemo(
        () => isPaymentInstrumentAvailable(paymentGroup, VALID_PAYMENT_GROUP_TYPES.CRED),
        [paymentGroup],
    );

    // force enable Juspay SDK for iOS
    // Todo- Remove this after all IOS users have migrated to version code 419 or above
    const forceEnableSDK = useMemo(() => isCredPayGroupExists, [isCredPayGroupExists]);

    // ── V3 auth token flag ─────────────────────────────────────────────────────
    const { checkoutWebWP } = useContext(CheckoutWebDynamicConfigContext);
    const { isJuspayV3Enabled: isJuspayV3EnabledFromConfig } = useContext(ConfigContext);
    const isV3Enabled = useMemo(() => {
        const contextEnabled = isJuspayV3EnabledFromConfig?.unwrapOrElse(() => false);
        if (contextEnabled) {
            return true;
        }
        try {
            const config = safeJsonParse<CheckoutWebWPConfig>(checkoutWebWP[CHECKOUT_WEB_WP.CW_WP_CONFIG]);
            return Boolean(config?.[CHECKOUT_WEB_WP_CONFIG_KEYS.ENABLE_JUSPAY_V3_AUTH_TOKEN]);
        } catch {
            return false;
        }
    }, [checkoutWebWP, isJuspayV3EnabledFromConfig]);

    // When V3 is enabled, derive tenant key from payment group → root → default.
    // For Instamart BL, use both SW-FOOD and DASH tenants.
    // When V3 is off, use root-level tenant as before.
    const tenant = useMemo(() => {
        if (!isV3Enabled) return paymentResponse.tenant;
        if (isBusinessCategoryInstamart()) return `${(ActiveTenant["SW-FOOD"], ActiveTenant["DASH"])}`;
        return paymentResponse.tenant;
    }, [isV3Enabled, paymentResponse]);

    const { isSdkAvailable, clientAuthTokenData, featuresAvailability, fetchingJuspaySdk, fetchJuspayClientAuthToken } =
        useJuspaySdkState(tenant, merchantId, forceEnableSDK, isV3Enabled);

    return {
        clientAuthTokenData,
        isSdkAvailable: !!isSdkAvailable,
        featuresAvailability,
        fetchingJuspaySdk,
        fetchJuspayClientAuthToken,
    };
};

export default useJuspaySdk;
